/**
 * Device detail (Wave-4 console). Opened from the "Manage" action on a bound-device
 * row. Shows the device's activity at a glance (last seen, status) and its facts
 * (fingerprint, customer, grace), and exposes the one control that matters here:
 * revoke (deactivate) the device. Server enforces (009). No separate device-detail
 * endpoint exists — the row already carries everything, so we render from the passed
 * `device` and mutate via `adminDeactivateDevice`.
 */
import { confirmAction } from "../ui/confirmAction.js";
import { useState } from "react";
import { useServices } from "../app/context.js";
import type { DeviceDetail } from "@genz/platform";
import { ActionBar } from "../ui/ActionBar.js";
import { CopyButton } from "../ui/CopyButton.js";
import {
  FactRows,
  StatCards,
  StatusBadge,
  fmtDateTime,
  fmtRelative,
} from "./detail-shared.js";

export function DeviceDetailPanel({
  device,
  customerLabel,
  onChanged,
  onClose,
}: {
  device: DeviceDetail;
  /** Optional friendly customer/store label for the facts row. */
  customerLabel?: string | null;
  onChanged?: () => void;
  onClose?: () => void;
}) {
  const { client } = useServices();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revoke() {
    if (!await confirmAction("Revoke this device? It will need to re-activate to use the app again.")) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await client.adminDeactivateDevice(device.device_id);
      onChanged?.();
      onClose?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /* The bar is a SIBLING of `.detail`, not a child of it (Z0 fix round P-2). A drawer's
     `.drawer__body` is a scrolling column and rule 7's bar is sticky over it; the shared
     clearance that keeps a focused control out from behind the bar comes from
     `:where(:has(> .actionbar))`, which only matches when the bar is a DIRECT child of that
     column. One wrapper and the column reserves nothing. */
  return (
    <>
    <div className="detail" data-testid="device-detail">
      <StatCards
        cards={[
          { label: "Last seen", value: fmtRelative(device.last_seen_at), testid: "device-kpi-last-seen" },
          {
            label: "Status",
            value: device.active ? "Active" : "Grace lapsed",
            sub: device.grace_until ? `grace until ${fmtDateTime(device.grace_until)}` : undefined,
            testid: "device-kpi-status",
          },
        ]}
      />

      <FactRows
        testid="device-facts"
        rows={[
          { label: "Fingerprint", value: <span className="id-inline"><code>{device.fingerprint}</code><CopyButton value={device.fingerprint} label="Copy fingerprint" /></span> },
          { label: "Customer", value: customerLabel ?? device.customer_id ?? "—" },
          { label: "Last seen", value: fmtDateTime(device.last_seen_at) },
          { label: "Grace until", value: device.grace_until ? fmtDateTime(device.grace_until) : "—" },
          { label: "Status", value: <StatusBadge status={device.active ? "active" : "grace_lapsed"} /> },
        ]}
      />

      {error && <p role="alert" data-testid="device-detail-error">{error}</p>}
    </div>

      {/* Rule 7. A drawer's actions live in the bar too. This one carries NO primary, which
          the primitive allows on purpose: revoking a device is destructive, not the loud
          invitation a page's commit is. */}
      <ActionBar label="Device actions">
        <button
          type="button"
          className="btn btn--danger"
          disabled={busy}
          onClick={() => void revoke()}
          data-testid="device-revoke"
        >
          {busy ? "Revoking…" : "Revoke device"}
        </button>
      </ActionBar>
    </>
  );
}
